import { Formik } from "formik";
import React, { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LandingFormFieldInput,
  LandingFormFieldSubmit,
} from "../../../components/landing/formFields";
import LandingWrapper from "../../../components/landing/landing";
import { routeUrls, routeVariables } from "../../../navigation/routeUrls";

import { useMsal } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { decode as base64_decode } from "base-64";
import { useDispatch, useSelector } from "react-redux";
import {
  useLoginMutation,
  useLoginSsoCredsMutation,
} from "../../../api/network/authApiService";
import { setPreLoginUserData } from "../../../api/store/commonSlice";
import svgIcons from "../../../assets/svg/svgIcons";
import {
  getMsalConfig,
  msalLoginRequestParams,
  setDynamicMsalAuthConfig,
} from "../../../authConfig";
import { APP_CONFIG, SSO_ENABLED } from "../../../constants/constants";
import { useLoginHandler } from "../../../hooks/loginHandler";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import "./login.scss";
import {
  emailValidationRegex,
  formInitialValues,
  TFormFieldNames,
  YupValidationSchema,
} from "./validation";

interface ScreenLoginProps {
  showSessionExpiredMessage?: boolean;
}

const ScreenLogin: FC<ScreenLoginProps> = ({ showSessionExpiredMessage }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "loginScreen",
  });
  const { t: tMain } = useTranslation();
  const { t: tAuthAzureCallback } = useTranslation("translation", {
    keyPrefix: "authAzureCallbackScreen",
  });
  const routeParams = useParams();
  const customEnv = routeParams?.[routeVariables.customEnv];
  const { setLocalStorageItem, removeLocalStorageItem } = useLocalStorage();
  const dispatch = useDispatch();

  // all conditional logic for login behaviour is moved to hooks/loginHandler.ts
  const loginHandler = useLoginHandler({ screen: "loginScreen" });

  const emailInputRef = React.useRef<HTMLInputElement>(null);
  const ssoLoginButtonRef = React.useRef<HTMLButtonElement>(null);
  const emailLookupTimeoutRef = React.useRef<number | null>(null);
  const lastSsoLookupEmailRef = React.useRef<string>("");
  useEffect(() => {
    emailInputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [loginApiTrigger, { isLoading }] = useLoginMutation();
  const [loginSsoCredsApiTrigger, { isLoading: isSsoCredsLoading }] =
    useLoginSsoCredsMutation();

  useEffect(() => {
    return () => {
      if (emailLookupTimeoutRef.current) {
        window.clearTimeout(emailLookupTimeoutRef.current);
      }
    };
  }, []);

  const queueSsoLookup = (emailValue: string) => {
    const email = emailValue.trim().toLowerCase();

    if (!emailValidationRegex.test(email)) {
      lastSsoLookupEmailRef.current = "";
      dispatch(
        setPreLoginUserData({
          email: emailValue,
          sso: undefined,
          clientid: undefined,
          tenantid: undefined,
        })
      );
      return;
    }

    if (emailLookupTimeoutRef.current) {
      window.clearTimeout(emailLookupTimeoutRef.current);
    }

    emailLookupTimeoutRef.current = window.setTimeout(() => {
      if (lastSsoLookupEmailRef.current === email) return;

      lastSsoLookupEmailRef.current = email;
      loginSsoCredsApiTrigger({ email })
        .unwrap()
        .then((ssoData) => {
          dispatch(
            setPreLoginUserData({
              email,
              sso: ssoData?.sso,
              clientid: ssoData?.clientid,
              tenantid: ssoData?.tenantid,
            })
          );
        })
        .catch(() => {
          dispatch(
            setPreLoginUserData({
              email,
              sso: undefined,
              clientid: undefined,
              tenantid: undefined,
            })
          );
        });
    }, 450);
  };

  useEffect(() => {
    if (showSessionExpiredMessage) {
      // console.log("===== D - showSessionExpiredMessage flag");
      toast.error(t("toast.session_expired"));
    }
  }, [showSessionExpiredMessage]);

  // prevent back button for all browsers including safari (macos, ios)
  // useEffect(() => {
  //   const handlePopState = (event: PopStateEvent) => {
  //     // window.history.go(1);
  //     window.history.pushState(null, '', routeUrls.landingPage);
  //   };
  //   window.addEventListener('popstate', handlePopState);
  //   return () => {
  //     window.removeEventListener('popstate', handlePopState);
  //   };
  // }, []);
  // useEffect(() => {
  //   (window as any).preventBack = () => {
  //     window.history.forward();
  //   }
  //   setTimeout("preventBack()", 0);
  // }, []);

  // SSO with MS Azure AD via MSAL
  const userAuthInProgress = useSelector(
    (state: { commonReducer: { userAuthInProgress?: boolean } }) =>
      state.commonReducer.userAuthInProgress
  );
  const ssoEnabledForEmail = useSelector(
    (state: { commonReducer: { preLoginUserData?: { sso?: boolean } } }) =>
      state.commonReducer.preLoginUserData?.sso === true
  );
  const ssoAzureConfig = useSelector(
    (state: {
      commonReducer: {
        preLoginUserData?: { clientid?: string; tenantid?: string };
      };
    }) => ({
      clientid: state.commonReducer.preLoginUserData?.clientid,
      tenantid: state.commonReducer.preLoginUserData?.tenantid,
    })
  );

  const {
    // instance: msalInstance, // for future use to check accounts or other info if needed for conditional rendering
    // accounts: msalAccounts, // for future use to check accounts or other info if needed for conditional rendering
    inProgress: msalInProgress,
  } = useMsal();

  // focus SSO login button when SSO is enabled and creds are loaded, but only if not in the middle of an SSO login flow (msalInProgress is "none"), otherwise the button loses focus when loading state changes during the flow
  useEffect(() => {
    if (!ssoEnabledForEmail) return;
    if (isSsoCredsLoading) return;
    if (msalInProgress !== "none") return;

    ssoLoginButtonRef.current?.focus();
  }, [ssoEnabledForEmail, isSsoCredsLoading, msalInProgress]);

  // dynamic MSAL config based on email input for SSO login with different Azure AD tenants
  const dynamicSsoClientId = ssoAzureConfig?.clientid || "";
  const dynamicSsoTenantId = ssoAzureConfig?.tenantid || "";
  const dynamicSsoMsalConfig = getMsalConfig({
    clientId: dynamicSsoClientId,
    tenantId: dynamicSsoTenantId,
  });

  const handleLoginWithCompanyPortal = async () => {
    setDynamicMsalAuthConfig({
      clientId: dynamicSsoClientId,
      tenantId: dynamicSsoTenantId,
    });

    const dynamicMsalInstance = new PublicClientApplication(
      dynamicSsoMsalConfig
    );

    await dynamicMsalInstance.initialize();
    await dynamicMsalInstance.loginRedirect(msalLoginRequestParams);
  };

  // If custom env is found in URL, save it to local storage and redirect to login page
  if (!!customEnv) {
    if (customEnv === "clear") {
      removeLocalStorageItem(localStorageKeys.TEMP_API_SERVER);
      toast.info(t("toast.custom_env_cleared"));
    } else {
      setLocalStorageItem(
        localStorageKeys.TEMP_API_SERVER,
        base64_decode(customEnv)
      );
      toast.info(t("toast.custom_env_set"));
    }
    return <Navigate to={routeUrls.loginPage} />;
  }

  return (
    <LandingWrapper>
      <Formik
        initialValues={formInitialValues}
        validationSchema={YupValidationSchema}
        onSubmit={(values, { setSubmitting }) => {
          if (APP_CONFIG.TOASTS.INFO) toast.info(t("toast.logging_you_in"));
          loginApiTrigger({ email: values.email, password: values.password })
            .unwrap()
            .then(loginHandler.loginSuccessHandler) // all conditional logic is moved to hooks/loginHandler.ts
            .catch(loginHandler.loginErrorHandler)
            .finally(() => {
              setSubmitting(false);
            });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          dirty,
        }) => {
          const invalidFields = Object.keys(errors).filter(
            (key) => !!errors[key as TFormFieldNames]
          );
          const isFormValid = invalidFields.length === 0 && dirty;

          return (
            <form
              className="p-8 bg-white grid grid-cols-6 gap-6 rounded-3xl shadow-2xl"
              onSubmit={handleSubmit}
            >
              {userAuthInProgress ? (
                <>
                  <h2 className="col-span-6 text-3xl font-bold text-heading-black">
                    {tAuthAzureCallback("heading")}
                  </h2>

                  <p className="col-span-6 text-base text-gray-500">
                    {tAuthAzureCallback("sub_heading")}
                  </p>
                  <img src={svgIcons.LoadingAnimation} alt="loading" />
                </>
              ) : (
                <>
                  <h2 className="col-span-6 text-3xl font-bold text-heading-black">
                    {tMain("login")}
                  </h2>

                  <LandingFormFieldInput
                    ref={emailInputRef}
                    label={tMain("email_address")}
                    type="email"
                    id="Email"
                    name="email"
                    placeholder={tMain("email_address_placeholder")}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      // to prevent unnecessary API calls for SSO creds when SSO is not enabled
                      if (!SSO_ENABLED) {
                        handleChange(event);
                        return;
                      }
                      dispatch(
                        setPreLoginUserData({
                          email: event.target.value || "",
                          sso: undefined,
                          clientid: undefined,
                          tenantid: undefined,
                        })
                      );
                      handleChange(event);
                      queueSsoLookup(event.target.value || "");
                    }}
                    onBlur={handleBlur}
                    value={values.email}
                    touched={touched.email}
                    error={errors.email}
                    disabled={msalInProgress !== "none"} // || isSsoCredsLoading - field looses focus when loading state changes, so only disable during msal login flow
                    loading={isSsoCredsLoading}
                  />

                  {/* Login with Company Portal */}
                  {ssoEnabledForEmail && (
                    <>
                      <div className="col-span-6">
                        <LandingFormFieldSubmit
                          ref={ssoLoginButtonRef}
                          label={t("login_company_portal")}
                          variant="primary"
                          type="button"
                          disabled={
                            isSubmitting ||
                            isLoading ||
                            isSsoCredsLoading ||
                            msalInProgress !== "none"
                          }
                          onClick={handleLoginWithCompanyPortal}
                        />
                      </div>
                      <div className="col-span-6">
                        <div className="relative text-center text-heading-gray888">
                          <hr className="mt-3 mb-5" />
                          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 uppercase">
                            {t("or")}
                          </span>
                          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 mt-6 w-60 text-sm">
                            {t("or_enter_password_below")}
                          </span>
                        </div>
                      </div>
                    </>
                  )}

                  <LandingFormFieldInput
                    label={tMain("password")}
                    type="password"
                    id="Password"
                    name="password"
                    placeholder={tMain("password_placeholder")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                    touched={touched.password}
                    error={errors.password}
                    disabled={msalInProgress !== "none"} // || isSsoCredsLoading - field looses focus when loading state changes, so only disable during msal login flow
                  />

                  <div className="col-span-6">
                    <p className="flex justify-end mt-4 text-sm sm:mt-0">
                      <Link
                        to={routeUrls.forgotPassword}
                        className="font-display font-semibold text-accent-blue-bright"
                      >
                        {tMain("forgot_your_password")}
                      </Link>
                    </p>
                  </div>

                  {/* Loader */}
                  <div className="col-span-6">
                    {isLoading || isSsoCredsLoading ? (
                      <div className="flex justify-center mt-0">
                        <img
                          src={svgIcons.LoadingAnimation}
                          alt="Loading animation"
                          className="w-12 h-12"
                        />
                      </div>
                    ) : (
                      <LandingFormFieldSubmit
                        label={tMain("login")}
                        variant="primary"
                        disabled={
                          !isFormValid ||
                          isSubmitting ||
                          isLoading ||
                          isSsoCredsLoading ||
                          msalInProgress !== "none"
                        }
                      />
                    )}
                  </div>
                </>
              )}
            </form>
          );
        }}
      </Formik>
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <pre className="no-print fixed top-0 right-0 bg-amber-600 text-white z-overlay-primary text-xs px-2">
          {JSON.stringify(
            {
              ssoEnabledForEmail,
              dynamicSsoClientId,
              dynamicSsoTenantId,
              dynamicSsoMsalAuth: dynamicSsoMsalConfig.auth,
            },
            null,
            2
          )}
        </pre>
      )}
    </LandingWrapper>
  );
};
export default ScreenLogin;
